import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import { Button, DatePicker, Modal, Space } from "antd";
import dayjs from "dayjs";
import { CalendarDays } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useData } from "../../context/DataContext";
import "./BookingCalendarModal.css";

const BookingCalendarModal = ({}) => {
  const { data, loading } = useData();
  const { bookings, rooms } = data || {};
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [calendarData, setCalendarData] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const calendarRef = useRef();

  const toggleModal = () => {
    setIsModalVisible((prev) => !prev);
    if (isModalVisible) {
      setSelectedDate(null); // Reset selected date when modal is closed
    }
  };

  useEffect(() => {
    calculateRoomsWithBookings();
  }, [rooms, bookings]);

  const calculateRoomsWithBookings = useCallback(async () => {
    try {
      const roomsWithBookings = rooms?.map((room) => {
        const roomBookings = bookings
          ?.filter((booking) =>
            booking?.room?.some((r) => Number(r.id) === Number(room.id)),
          )
          ?.map((booking) => ({
            start: booking.check_in,
            end: booking.check_out,
          }));

        return {
          room: {
            ...room,
            bookedDates: roomBookings,
          },
          isAvailable: room.status,
        };
      });

      setCalendarData(roomsWithBookings);
    } catch (error) {
      console.error("Error fetching rooms or bookings:", error);
    }
  }, [rooms, bookings]);

  useEffect(() => {
    if (isModalVisible) {
      calculateRoomsWithBookings();

      setTimeout(() => {
        calendarRef.current?.getApi()?.updateSize();
        calendarRef.current?.getApi()?.gotoDate(dayjs().format("YYYY-MM-DD"));
      }, 300);
    }
  }, [isModalVisible, calculateRoomsWithBookings]);

  const handleDateChange = (date) => {
    if (date && calendarRef.current) {
      setSelectedDate(date); // update selected date
      const calendarApi = calendarRef.current.getApi();
      calendarApi.gotoDate(date.format("YYYY-MM-DD"));
    }
  };

  // Build the events array
  const events = [
    ...(selectedDate
      ? [
          {
            title: "📍 Selected Date",
            start: selectedDate.format("YYYY-MM-DD"),
            end: selectedDate.add(1, "day").format("YYYY-MM-DD"),
            allDay: true,
            backgroundColor: "#E0F0FF",
            borderColor: "#1890ff",
            textColor: "#000",
            className: "selected-date", // Custom class for extra styling
          },
        ]
      : []),
    ...calendarData.flatMap((item) =>
      item.room.bookedDates.length === 0
        ? [
            {
              title: `Room ${item.room.room_number} - Available`,
              start: dayjs().format("YYYY-MM-DD"),
              end: dayjs().endOf("year").format("YYYY-MM-DD"),
              allDay: true,
              backgroundColor: "#D3F9D8",
              borderColor: "#A3E635",
              textColor: "#000",
            },
          ]
        : item.room.bookedDates.map((dateRange) => ({
            title: `Room ${item.room.room_number} - Occupied`,
            start: dayjs(dateRange.start).format("YYYY-MM-DD"),
            end: dayjs(dateRange.end).format("YYYY-MM-DD"),
            allDay: true,
            backgroundColor: "#FF6B6B",
            borderColor: "#FF6B6B",
            textColor: "#fff",
          })),
    ),
  ];

  return (
    <>
      <Button
        style={{ border: "none" }}
        onClick={toggleModal}
        icon={<CalendarDays />}
      />

      <Modal
        /* title="Booking Calendar" */
        open={isModalVisible}
        onCancel={toggleModal}
        footer={null}
        width="100%"
        style={{ maxWidth: 1200 }}
      >
        <Space style={{ marginBottom: 16 }}>
          <DatePicker
            onChange={handleDateChange}
            disabledDate={(current) =>
              current && current < dayjs().startOf("day")
            }
          />
        </Space>

        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, interactionPlugin, timeGridPlugin]}
          initialView="dayGridWeek"
          headerToolbar={{
            left: "prev,next today",
            center: "title",
            right: "",
          }}
          buttonText={{
            today: "Today",
          }}
          /* headerToolbar={{
            left: '',
            center: 'prev today next',
            right: '',
          }} */
          visibleRange={() => {
            const startOfWeek = dayjs().startOf("week");
            const today = dayjs().startOf("day");
            const endOfWeek = dayjs().endOf("week");
            return {
              start: today.format("YYYY-MM-DD"),
              end: endOfWeek.add(1, "day").format("YYYY-MM-DD"),
            };
          }}
          events={events}
          dayHeaderFormat={{
            weekday: "short",
            day: "numeric",
            month: "short",
          }}
          height="auto"
        />
      </Modal>
    </>
  );
};

export default BookingCalendarModal;
